import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';

/**
 * Shared 32×32 bordered icon button used across the Jobs and Backfill
 * listing rows (and any future listing that wants the same action
 * grammar — View / Add task / Delete).
 *
 * Centralises the visual spec so every row-level icon action across the
 * app has the same hit-target, border radius, hover tint and tooltip
 * treatment. Variants:
 *   - `default`: neutral border, accent on hover.
 *   - `danger` : neutral border, red on hover (for destructive actions).
 *
 * Busy state swaps the glyph for a spinner so the user has visible
 * feedback a mid-flight action was picked up; useful for the Delete
 * button pattern where repeated clicks would otherwise silently no-op.
 */
@Component({
  selector: 'lib-row-icon-button',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TooltipModule],
  templateUrl: './row-icon-button.html',
  styleUrl: './row-icon-button.scss',
})
export class RowIconButton {
  /** PrimeIcons glyph class, e.g. "pi pi-eye". */
  readonly icon = input.required<string>();

  /** Tooltip text shown on hover. Mandatory because icon-only buttons
   *  need an accessible on-screen name — don't skip it. */
  readonly tooltip = input.required<string>();

  /** Where PrimeNG anchors the tooltip relative to the button. */
  readonly tooltipPosition = input<'top' | 'left' | 'right' | 'bottom'>('top');

  /** Accessible name read by assistive tech. Defaults to the tooltip
   *  when the host doesn't need a longer screen-reader label. */
  readonly ariaLabel = input<string | null>(null);

  readonly variant = input<'default' | 'danger'>('default');

  readonly disabled = input<boolean>(false);

  /** True while the host is processing the action — swaps icon for a
   *  spinner and marks the button busy. */
  readonly busy = input<boolean>(false);

  @Output() readonly action = new EventEmitter<MouseEvent>();

  onClick(event: MouseEvent): void {
    // Row-level buttons sit inside a (click)-handled row; stop here so
    // the row's own navigation doesn't fire. Host can still read the
    // event via (action) if it needs something more specific.
    event.stopPropagation();
    if (this.disabled() || this.busy()) return;
    this.action.emit(event);
  }
}
